import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Paper,
  LinearProgress,
  alpha,
  Snackbar,
  Alert,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Reply as ReplyIcon,
  Info as InfoIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh,
} from "@mui/icons-material";
import { decryptText, decryptAppId } from "../utils/encryption";
import s3Utils from "../utils/s3Utils";
import PageTitle from "../components/PageTitle";

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
  },
  secondary: {
    main: "#ec4899",
    light: "#f472b6",
    dark: "#db2777",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

const NCReply = () => {
  const location = useLocation();

  // Function to get parameters from URL
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        return decryptAppId(encryptedAssessorId);
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        return decryptText(encryptedAssessmentId);
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  const [orgId] = useState(getOrgIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [assessorId] = useState(getAssessorIdFromQuery());
  const [assessmentId] = useState(getAssessmentIdFromQuery());
  const [nonConformities, setNonConformities] = useState([]);
  const [loadingNC, setLoadingNC] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  // Assessor stage state
  const [assessorStage, setAssessorStage] = useState(null);
  const [loadingStage, setLoadingStage] = useState(false);

  // NC Reply states
  const [ncReplies, setNcReplies] = useState({});
  const [submittingReplies, setSubmittingReplies] = useState({});
  const [allRepliesSubmitted, setAllRepliesSubmitted] = useState(false);
  const [editingReplies, setEditingReplies] = useState({});
  const [labAcceptance, setLabAcceptance] = useState({});

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // Root Cause Analysis and Corrective Action states
  const [ncRemarks, setNcRemarks] = useState({});
  const [correctiveAction, setCorrectiveAction] = useState({});
  const [rootCause, setRootCause] = useState({});
  const [correctiveActionFiles, setCorrectiveActionFiles] = useState({});
  const [rootCauseFiles, setRootCauseFiles] = useState({});
  const [savingRemarks, setSavingRemarks] = useState({});
  const [expandedReplyHistory, setExpandedReplyHistory] = useState({});
  const [sendingToAssessor, setSendingToAssessor] = useState(false);
  const [sendToAssessorConfirmDialogOpen, setSendToAssessorConfirmDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [ncReplyToDelete, setNcReplyToDelete] = useState(null);
  const [applicationStage, setApplicationStage] = useState(null);
  const navigate = useNavigate();



  // Show Snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Fetch Non-Conformities from API
  const fetchNonConformities = async () => {
    try {
      setLoadingNC(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetNCMain?appid=${appId}&assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const response = await axios.get(url);

      if (
        response.data.isstatus &&
        response.data.nc &&
        Array.isArray(response.data.nc)
      ) {
        const formattedNC = response.data.nc.map((item) => ({
          id: item.id,
          clause: item.clauseno,
          remarks: item.remarks || "",
          description: item.description || "",
          document: item.document || "",
          ncid: item.ncid,
          cabReply: item.cabReply || "",
          cabReplyDate: item.cabReplyDate || null,
          isLabAccept: item.isLabAccept, // Keep as string for proper comparison
          fileupload: item.fileupload || "", // Add fileupload field
          assessorRemarks: item.assessorRemarks || "",
          assessorRemarksBy: item.assessorRemarksBy || "",
          assessorRemarksDate: item.assessorRemarksDate || null,
          stage: item.stage !== undefined ? item.stage : null,
          isclsosed: item.isclsosed || "False",
          ncreply: item.ncreply || [], // Add ncreply array
        }));

        setNonConformities(formattedNC);

        // Initialize reply states
        const initialReplies = {};
        const initialAcceptance = {};
        formattedNC.forEach((nc) => {
          initialReplies[nc.id] = nc.cabReply || "";
          // Convert string values to boolean for UI state
          if (nc.isLabAccept === "1") {
            initialAcceptance[nc.id] = true;
          } else if (nc.isLabAccept === "0") {
            initialAcceptance[nc.id] = false;
          } else {
            initialAcceptance[nc.id] = null;
          }
        });
        setNcReplies(initialReplies);
        setLabAcceptance(initialAcceptance);

        // Check if all NCs have replies
        checkAllRepliesStatus(formattedNC);
      } else {
        setNonConformities([]);
      }
    } catch (error) {
      showSnackbar("Error loading non-conformities", "error");
      setNonConformities([]);
    } finally {
      setLoadingNC(false);
    }
  };

  // Check if all NCs have replies
  const checkAllRepliesStatus = (ncList = nonConformities) => {
    if (ncList.length === 0) {
      setAllRepliesSubmitted(false);
      return;
    }

    const allHaveReplies = ncList.every((nc) => {
      // Check if NC has been saved (has cabReplyDate)
      const hasCabReplyDate = !!nc.cabReplyDate;

      return hasCabReplyDate;
    });

    setAllRepliesSubmitted(allHaveReplies);
  };

  // Check if all NCs have acceptance decisions (Yes or No)
  const checkAllAcceptanceDecisions = () => {
    if (nonConformities.length === 0) {
      return false;
    }

    return nonConformities.every((nc) => {
      const hasAcceptanceDecision =
        labAcceptance[nc.id] !== null && labAcceptance[nc.id] !== undefined;

      return hasAcceptanceDecision;
    });
  };

  // Fetch assessor stage
  const fetchAssessorStage = async () => {
    if (!appId || !assessorId) {
      console.warn("Missing appId or assessorId for stage check");
      return;
    }

    try {
      setLoadingStage(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/nc-main/get-assessor-stage/${appId}/${assessorId}`
      );

      if (response.data.success) {
        const stage = response.data.data.stage;
        setAssessorStage(stage);

      } else {
        console.warn("Failed to fetch assessor stage:", response.data.message);
      }
    } catch (error) {
      console.error("❌ Error fetching assessor stage:", error);
    } finally {
      setLoadingStage(false);
    }
  };

  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/laboratory/application-stage/${appId}`);
      const result = await response.json();

      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        setApplicationStage('1'); // Default to 1
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage('1'); // Default to 1
    }
  };

  // Handle lab acceptance change
  const handleLabAcceptanceChange = async (ncId, acceptance) => {
    setLabAcceptance((prev) => ({
      ...prev,
      [ncId]: acceptance,
    }));

    // If user clicks "Yes" or "No", show file upload option
    if (acceptance === true || acceptance === false) {
      // Only clear files if not in edit mode or if no existing files
      const isInEditMode = editingReplies[ncId];
      const nc = nonConformities.find((n) => n.id === ncId);
      const hasExistingFiles = nc && nc.fileupload && nc.fileupload.trim();

      if (!isInEditMode || !hasExistingFiles) {
        setUploadedFiles((prev) => ({
          ...prev,
          [ncId]: [],
        }));
      }
      // If in edit mode with existing files, keep them (already loaded by enableEditMode)
    }

    // For now, we'll just store locally and save with the reply
    // You can uncomment this when the API endpoint is ready
    /*
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/nc-main/lab-acceptance/${ncId}`,
        { isLabAccept: acceptance }
      );

      if (response.data.success) {
        showSnackbar(`NC ${acceptance ? 'accepted' : 'not accepted'} successfully!`, "success");
        
        // Update the NC in the list to show the acceptance status
        setNonConformities(prev => prev.map(nc => 
          nc.id === ncId 
            ? { ...nc, isLabAccept: acceptance }
            : nc
        ));
        
        // Check all replies status after acceptance change
        setTimeout(() => checkAllRepliesStatus(), 0);
      } else {
        throw new Error(response.data.error || "Failed to save acceptance status");
      }
    } catch (error) {
      console.error("❌ Error saving lab acceptance:", error);
      showSnackbar(
        error.response?.data?.error || error.message || "Error saving acceptance status", 
        "error"
      );
      // Revert the state on error
      setLabAcceptance(prev => ({
        ...prev,
        [ncId]: labAcceptance[ncId]
      }));
    }
    */
  };

  // Handle file upload for NC
  const handleFileUpload = async (ncId, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFiles((prev) => ({ ...prev, [ncId]: true }));

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("orgId", orgId);
        formData.append("section", "nc-evidence");
        formData.append("fieldName", `nc_${ncId}_${Date.now()}`);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/upload/single`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setUploadProgress((prev) => ({
                ...prev,
                [ncId]: percentCompleted,
              }));
            },
          }
        );

        if (response.data.success) {
          return {
            fileName: file.name,
            url: response.data.data.url,
            directUrl: response.data.data.directUrl,
          };
        } else {
          throw new Error(response.data.error || "Upload failed");
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      setUploadedFiles((prev) => ({
        ...prev,
        [ncId]: [...(prev[ncId] || []), ...uploadResults],
      }));

      setUploadProgress((prev) => ({ ...prev, [ncId]: 0 }));
      showSnackbar(
        `${uploadResults.length} file(s) uploaded successfully!`,
        "success"
      );
    } catch (error) {
      console.error("❌ Error uploading files:", error);
      showSnackbar(
        error.response?.data?.error || error.message || "Error uploading files",
        "error"
      );
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [ncId]: false }));
    }
  };

  // Remove uploaded file
  const removeUploadedFile = (ncId, fileIndex) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [ncId]: prev[ncId]?.filter((_, index) => index !== fileIndex) || [],
    }));
  };

  // View uploaded file
  const viewUploadedFile = (fileUrl, fileName) => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    } else {
      showSnackbar("File URL not available", "error");
    }
  };

  // Handle Corrective Action file upload
  const handleCorrectiveActionFileUpload = async (ncId, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("orgId", orgId);
        formData.append("section", "nc-corrective-action");
        formData.append("fieldName", `corrective_${ncId}_${Date.now()}`);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/upload/single`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          return {
            fileName: file.name,
            url: response.data.data.url,
          };
        } else {
          throw new Error(response.data.error || "Upload failed");
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      setCorrectiveActionFiles((prev) => ({
        ...prev,
        [ncId]: [...(prev[ncId] || []), ...uploadResults],
      }));

      showSnackbar("Corrective action file(s) uploaded!", "success");
    } catch (error) {
      showSnackbar("Error uploading corrective action files", "error");
    }
  };

  // Handle Root Cause file upload
  const handleRootCauseFileUpload = async (ncId, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("orgId", orgId);
        formData.append("section", "nc-root-cause");
        formData.append("fieldName", `rootcause_${ncId}_${Date.now()}`);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/upload/single`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          return {
            fileName: file.name,
            url: response.data.data.url,
          };
        } else {
          throw new Error(response.data.error || "Upload failed");
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      setRootCauseFiles((prev) => ({
        ...prev,
        [ncId]: [...(prev[ncId] || []), ...uploadResults],
      }));

      showSnackbar("Root cause file(s) uploaded!", "success");
    } catch (error) {
      showSnackbar("Error uploading root cause files", "error");
    }
  };

  // Handle reply text change
  const handleReplyChange = (ncId, value) => {
    setNcReplies((prev) => ({
      ...prev,
      [ncId]: value,
    }));

    // Check if all replies are filled after state update
    setTimeout(() => checkAllRepliesStatus(), 0);
  };

  // Save NC Remarks with Root Cause and Corrective Action
  const saveNCRemarks = async (ncId, remarks) => {
    // Validate Root Cause Analysis and Corrective Action fields
    if (!rootCause[ncId]?.trim()) {
      showSnackbar("Please enter Root Cause Analysis before saving", "warning");
      return;
    }

    if (!correctiveAction[ncId]?.trim()) {
      showSnackbar("Please enter Corrective Action before saving", "warning");
      return;
    }

    setSavingRemarks((prev) => ({ ...prev, [ncId]: true }));

    try {
      // Extract filenames from URLs (save only filenames, not full URLs)
      const correctiveActionUpload = correctiveActionFiles[ncId]
        ?.map((file) => {
          // Extract filename from URL
          const urlParts = file.url.split('/');
          return urlParts[urlParts.length - 1];
        })
        .join("~") || "";

      const rootCauseUpload = rootCauseFiles[ncId]
        ?.map((file) => {
          // Extract filename from URL
          const urlParts = file.url.split('/');
          return urlParts[urlParts.length - 1];
        })
        .join("~") || "";

      const payload = {
        ncId: ncId.toString(),
        replyRemarks: '',
        replyById: localStorage.getItem("orgId") || "",
        correctiveAction: correctiveAction[ncId] || "",
        correctiveActionupload: correctiveActionUpload,
        rootcause: rootCause[ncId] || "",
        rootcauseupload: rootCauseUpload,
        replyby: 'Lab',
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/ncreply`,
        payload
      );

      if (response.data.isstatus || response.data.success) {
        showSnackbar("Remarks saved successfully!", "success");
        try {
          const updateStatusPayload = {
            Ncid: ncId,
            Status: "0",
            stage: "1",
          };

          const updateStatusUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Updatencstatus?${new URLSearchParams(updateStatusPayload).toString()}`;
          const updateResponse = await axios.put(updateStatusUrl);

          if (updateResponse.data.isstatus) {
            console.log("✅ NC status updated successfully");
          } else {
            console.warn("⚠️ Reply saved but failed to update NC status");
          }
        } catch (statusError) {
          console.error("❌ Error updating NC status:", statusError);
          // Don't throw error here as the main reply was successful
        }

        // Clear/reset the fields after successful save
        setCorrectiveAction((prev) => ({
          ...prev,
          [ncId]: "",
        }));
        setRootCause((prev) => ({
          ...prev,
          [ncId]: "",
        }));
        setCorrectiveActionFiles((prev) => ({
          ...prev,
          [ncId]: [],
        }));
        setRootCauseFiles((prev) => ({
          ...prev,
          [ncId]: [],
        }));

        // Refresh NC data to show updated reply history
        await fetchNonConformities();
      } else {
        throw new Error(response.data.message || "Failed to save remarks");
      }
    } catch (error) {
      console.error("❌ Error saving NC remarks:", error);
      showSnackbar(
        error.response?.data?.message || error.message || "Error saving remarks",
        "error"
      );
    } finally {
      setSavingRemarks((prev) => ({ ...prev, [ncId]: false }));
    }
  };

  // Save individual NC reply
  const saveNCReply = async (ncId) => {
    const reply = ncReplies[ncId]?.trim();
    const acceptance = labAcceptance[ncId];

    // Require reply text for both "Yes" and "No" responses
    if ((acceptance === true || acceptance === false) && !reply) {
      const message =
        acceptance === true
          ? "Please enter a corrective action before saving"
          : "Please enter your response/explanation before saving";
      showSnackbar(message, "warning");
      return;
    }

    // Ensure isLabAccept is properly set (not null when false)
    if (acceptance === undefined || acceptance === null) {
      showSnackbar("Please select Yes or No before saving", "warning");
      return;
    }

    setSubmittingReplies((prev) => ({ ...prev, [ncId]: true }));

    try {
      // Prepare file upload URLs with ~ separator
      const fileUrls =
        uploadedFiles[ncId]?.map((file) => file.url).join("~") || "";

      // Ensure we're sending the correct data types
      const payload = {
        cabReply: reply || "", // Send empty string if no reply for "No" acceptance
        isLabAccept: acceptance === true ? true : false, // Explicitly convert to boolean
        fileupload: fileUrls, // Include uploaded file URLs
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/nc-main/cab-reply/${ncId}`,
        payload
      );

      if (response.data.success) {
        showSnackbar("Reply saved successfully!", "success");

        // Update the NC in the list to show it has been replied
        setNonConformities((prev) =>
          prev.map((nc) =>
            nc.id === ncId
              ? {
                ...nc,
                cabReply: reply || "",
                cabReplyDate: new Date().toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                }),
                isLabAccept: acceptance === true ? "1" : "0", // Convert boolean back to string
                fileupload: fileUrls || "", // Ensure it's never undefined
              }
              : nc
          )
        );


        // Exit edit mode
        setEditingReplies((prev) => ({ ...prev, [ncId]: false }));

        // Clear uploaded files state since they're now saved in the NC
        setUploadedFiles((prev) => ({ ...prev, [ncId]: [] }));

        // Check if all replies are now submitted
        checkAllRepliesStatus();
      } else {
        throw new Error(response.data.error || "Failed to save reply");
      }
    } catch (error) {
      console.error("❌ Error saving NC reply:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error status:", error.response?.status);

      showSnackbar(
        error.response?.data?.error || error.message || "Error saving reply",
        "error"
      );
    } finally {
      setSubmittingReplies((prev) => ({ ...prev, [ncId]: false }));
    }
  };

  // Enable edit mode for a reply
  const enableEditMode = (ncId, currentReply) => {
    setEditingReplies((prev) => ({ ...prev, [ncId]: true }));
    setNcReplies((prev) => ({ ...prev, [ncId]: currentReply }));

    // Load existing uploaded files for this NC
    const nc = nonConformities.find((n) => n.id === ncId);
    if (nc && nc.fileupload && nc.fileupload.trim()) {
      const existingFiles = nc.fileupload
        .split("~")
        .filter(Boolean)
        .map((url, index) => {
          // Extract filename from URL
          const fileName = url.split("/").pop() || `Document ${index + 1}`;
          return {
            fileName: fileName,
            url: url,
            directUrl: url,
          };
        });

      setUploadedFiles((prev) => ({
        ...prev,
        [ncId]: existingFiles,
      }));
    } else {
      // Clear uploaded files if no existing files
      setUploadedFiles((prev) => ({
        ...prev,
        [ncId]: [],
      }));
    }
  };

  // Cancel edit mode
  const cancelEditMode = (ncId) => {
    setEditingReplies((prev) => ({ ...prev, [ncId]: false }));
    // Reset to original reply if it exists
    const originalNC = nonConformities.find((nc) => nc.id === ncId);
    if (originalNC && originalNC.cabReply) {
      setNcReplies((prev) => ({ ...prev, [ncId]: originalNC.cabReply }));
    }

    // Reset uploaded files to original state
    if (originalNC && originalNC.fileupload && originalNC.fileupload.trim()) {
      const originalFiles = originalNC.fileupload
        .split("~")
        .filter(Boolean)
        .map((url, index) => {
          const fileName = url.split("/").pop() || `Document ${index + 1}`;
          return {
            fileName: fileName,
            url: url,
            directUrl: url,
          };
        });

      setUploadedFiles((prev) => ({
        ...prev,
        [ncId]: originalFiles,
      }));
    } else {
      // Clear uploaded files if no original files
      setUploadedFiles((prev) => ({
        ...prev,
        [ncId]: [],
      }));
    }
  };

  // Lightbox handlers
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Delete NC Reply function - show confirmation dialog
  const handleDeleteNCReply = (ncId) => {
    setNcReplyToDelete(ncId);
    setDeleteConfirmDialogOpen(true);
  };

  // Handle confirmed delete action
  const handleConfirmedDeleteNCReply = async () => {
    if (!ncReplyToDelete) return;

    try {
      console.log(`🗑️ Attempting to delete NC reply with ID: ${ncReplyToDelete}`);

      // Updated to use query parameters instead of URL parameters
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/da-nc/reply-delete?ncId=${ncReplyToDelete}`
      );

      if (response.data.success) {
        showSnackbar("NC reply deleted successfully!", "success");

        // Refresh the NC data to reflect the deletion
        await fetchNonConformities();
      } else {
        throw new Error(response.data.message || "Failed to delete NC reply");
      }
    } catch (error) {
      console.error("❌ Error deleting NC reply:", error);
      showSnackbar(
        error.response?.data?.message ||
        error.message ||
        "Failed to delete NC reply",
        "error"
      );
    } finally {
      setDeleteConfirmDialogOpen(false);
      setNcReplyToDelete(null);
    }
  };

  // Check if all NCs have Lab replies as the last entry in ncreply array
  const checkAllNCsHaveLabReplies = () => {
    if (nonConformities.length === 0) return false;

    // Filter out closed NCs - they don't need lab replies
    const openNCs = nonConformities.filter(nc => nc.isclsosed === "False");
    if (openNCs.length === 0) return false;

    return openNCs.every((nc) => {
      // Check if ncreply array exists and has replies
      if (!nc.ncreply || !Array.isArray(nc.ncreply) || nc.ncreply.length === 0) {
        return false;
      }

      // Get the last reply in the array (most recent)
      const lastReply = nc.ncreply[nc.ncreply.length - 1];

      // Check if the last reply is from Lab/Cab (not from Assessor)
      return lastReply.replyby === "Cab" || lastReply.replyby === "Lab";
    });
  };

  // Send to Assessor - Update Assessor Stage
  const handleSendToAssessor = async () => {
    if (!appId || !assessorId || !assessmentId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }

    try {
      setSendingToAssessor(true);

      const response = await axios.put(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessorStage`,
        null,
        {
          params: {
            appId: appId,
            assessorId: assessorId,
            assessmentId: assessmentId,
            stage: "5h",
          },
        }
      );

      if (response.data.isstatus || response.data.success) {
        showSnackbar("Successfully sent to assessor!", "success");
        // Refresh data
        await fetchNonConformities();
        await fetchAssessorStage();
        await getApplicationStage();
        setTimeout(() => {
          navigate(
            `/main/laboratory-update-details?orgId=${encodeURIComponent(
              location.search.split("orgId=")[1].split("&")[0]
            )}&appId=${encodeURIComponent(
              location.search.split("appId=")[1].split("&")[0]
            )}`
          );
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to send to assessor");
      }
    } catch (error) {
      console.error("❌ Error sending to assessor:", error);
      showSnackbar(
        error.response?.data?.message || error.message || "Error sending to assessor",
        "error"
      );
    } finally {
      setSendingToAssessor(false);
    }
  };

  // Handle confirmation for Send to Assessor
  const handleSendToAssessorConfirmation = () => {
    setSendToAssessorConfirmDialogOpen(true);
  };

  // Handle confirmed Send to Assessor action
  const handleConfirmedSendToAssessor = async () => {
    setSendToAssessorConfirmDialogOpen(false);
    await handleSendToAssessor();
  };

  // Handle stage update when Submit My Reply button is clicked
  const handleStageUpdate = async () => {
    if (!appId || !assessorId) {
      showSnackbar("Missing required parameters for stage update", "error");
      return;
    }

    try {
      // Show loading state
      // showSnackbar("Submitting stage update...", "info");

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/nc-main/stage-update/${appId}/${assessorId}`
      );

      if (response.data.success) {
        showSnackbar("Stage update submitted successfully! ✓", "success");

        setTimeout(() => {
          navigate(
            `/main/laboratory-update-details?orgId=${encodeURIComponent(
              location.search.split("orgId=")[1].split("&")[0]
            )}&appId=${encodeURIComponent(
              location.search.split("appId=")[1].split("&")[0]
            )}`
          );
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to update stage");
      }
    } catch (error) {
      console.error("❌ Error updating stage:", error);
      showSnackbar(
        error.response?.data?.message ||
        error.message ||
        "Error updating stage",
        "error"
      );
    }
  };
  useEffect(() => {
    getApplicationStage(appId);
  }, [appId]);

  useEffect(() => {
    fetchNonConformities();
    fetchAssessorStage();

  }, [appId, assessmentId, assessorId]);

  const hasNCs = nonConformities.length > 0;
  const isStage3 = assessorStage === "3";

  const allHaveAcceptanceDecisions = checkAllAcceptanceDecisions();
  const canSubmit =
    hasNCs &&
    allHaveAcceptanceDecisions &&
    allRepliesSubmitted &&
    !isStage3;

  return (
    <Box
      sx={{
        p: 1,
        backgroundColor: colors.grey[50],
        minHeight: "100vh",
      }}
    >
      <PageTitle title="NABL | Reply to Non-Conformities" />

      {/* Working Space Section */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
          background: "white",
          border: `1px solid ${colors.grey[200]}`,
        }}
      >
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <AssignmentIcon sx={{ color: colors.primary.main }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: colors.grey[900] }}
              >
                Non-Conformities - Reply by Lab
              </Typography>
              <Chip
                label={`${nonConformities.length} Non-Conformities`}
                color="primary"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          }
          sx={{
            borderBottom: `1px solid ${colors.grey[200]}`,
            pb: 2,
          }}
        />
        <CardContent sx={{ p: 2 }}>
          {loadingNC ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading non-conformities...
              </Typography>
            </Box>
          ) : nonConformities.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <InfoIcon sx={{ fontSize: 48, color: colors.grey[400], mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No Non-Conformities Found
              </Typography>
            </Box>
          ) : (
            <>
              {nonConformities.map((nc, index) => {
                // Debug logging for isclsosed status

                return (
                  <Paper
                    key={nc.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 3,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                      border: `1px solid ${colors.grey[200]}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: colors.grey[900],
                            }}
                          >
                            {`NC${String(index + 1).padStart(3, "0")}`}
                          </Typography>
                          <Chip
                            label={`Clause ${nc.clause}`}
                            size="small"
                            sx={{
                              bgcolor: alpha(colors.primary.main, 0.1),
                              color: colors.primary.main,
                              fontWeight: 600,
                            }}
                          />
                        </Box>

                        {nc.description && (
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.grey[800],
                              fontWeight: 500,
                              p: 2,
                              mb: 2,
                              backgroundColor: colors.grey[50],
                              borderRadius: 2,
                              borderLeft: `4px solid ${colors.error.main}`,
                            }}
                          >
                            {nc.description}
                          </Typography>
                        )}

                        {nc.remarks && (
                          <Typography
                            variant="body2"
                            sx={{ color: colors.grey[600], mb: 1 }}
                          >
                            <strong>Remarks:</strong> {nc.remarks}
                          </Typography>
                        )}

                        {nc.document && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 2,
                              mt: 2,
                            }}
                          >
                            {nc.document
                              .split("~")
                              .filter(Boolean)
                              .map((doc, docIndex) => {
                                const docLower = doc.toLowerCase();
                                const isImage = docLower.match(
                                  /\.(jpg|jpeg|png|gif|webp)$/
                                );
                                const isPdf = docLower.endsWith(".pdf");
                                const isExcel = docLower.match(/\.(xlsx|xls)$/);

                                return (
                                  <WorkingSpaceDocument
                                    key={docIndex}
                                    doc={doc}
                                    index={docIndex}
                                    isImage={isImage}
                                    isPdf={isPdf}
                                    isExcel={isExcel}
                                    colors={colors}
                                    onImageClick={
                                      isImage ? handleOpenLightbox : null
                                    }
                                  />
                                );
                              })}
                          </Box>
                        )}

                        {/* NC Reply Section */}
                        <Box
                          sx={{
                            mt: 3,
                            pt: 2,
                            borderTop: `1px solid ${colors.grey[200]}`,
                          }}
                        >
                          {nc.cabReplyDate && !editingReplies[nc.id] ? (
                            // Show saved reply with edit option
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: alpha(colors.success.main, 0.05),
                                borderRadius: 2,
                                border: `1px solid ${alpha(
                                  colors.success.main,
                                  0.2
                                )}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <CheckCircleIcon
                                    sx={{
                                      color: colors.success.main,
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: colors.success.dark,
                                    }}
                                  >
                                    Reply Saved{" "}
                                    {nc.isLabAccept === "1"
                                      ? "(Accepted)"
                                      : "(Not Accepted)"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: colors.grey[600] }}
                                  >
                                    on{" "}
                                    {new Date(
                                      nc.cabReplyDate
                                    ).toLocaleDateString()}
                                  </Typography>
                                </Box>
                                {applicationStage === '20' && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() =>
                                      enableEditMode(nc.id, nc.cabReply)
                                    }
                                    sx={{
                                      borderColor: colors.primary.main,
                                      color: colors.primary.main,
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          colors.primary.main,
                                          0.1
                                        ),
                                        borderColor: colors.primary.dark,
                                      },
                                    }}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </Box>
                              {nc.cabReply && nc.cabReply.trim() && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: colors.grey[800],
                                    fontStyle: "italic",
                                    backgroundColor: "white",
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: `1px solid ${colors.grey[200]}`,
                                  }}
                                >
                                  "{nc.cabReply}"
                                </Typography>
                              )}
                              {(!nc.cabReply || !nc.cabReply.trim()) &&
                                nc.isLabAccept === "0" && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colors.grey[600],
                                      fontStyle: "italic",
                                      backgroundColor: "white",
                                      p: 1.5,
                                      borderRadius: 1,
                                      border: `1px solid ${colors.grey[200]}`,
                                    }}
                                  >
                                    No corrective action required (Not Accepted)
                                  </Typography>
                                )}

                              {/* Display uploaded files if NC is accepted and has fileupload */}
                              {nc.isLabAccept === "1" &&
                                nc.fileupload &&
                                nc.fileupload.trim() && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        mb: 1.5,
                                        fontWeight: 600,
                                        color: colors.grey[700],
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <AttachFileIcon fontSize="small" />
                                      Supporting Documents:
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fit, minmax(200px, 1fr))",
                                        gap: 1.5,
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {nc.fileupload
                                        .split("~")
                                        .filter(Boolean)
                                        .map((fileUrl, index) => {
                                          // Extract filename from URL
                                          const fileName =
                                            fileUrl.split("/").pop() ||
                                            `Document ${index + 1}`;
                                          const fileExtension = fileName
                                            .split(".")
                                            .pop()
                                            ?.toLowerCase();

                                          // Determine file type icon
                                          let fileIcon = (
                                            <DescriptionIcon fontSize="small" />
                                          );
                                          if (
                                            [
                                              "jpg",
                                              "jpeg",
                                              "png",
                                              "gif",
                                            ].includes(fileExtension)
                                          ) {
                                            fileIcon = (
                                              <ImageIcon fontSize="small" />
                                            );
                                          } else if (fileExtension === "pdf") {
                                            fileIcon = (
                                              <PictureAsPdfIcon fontSize="small" />
                                            );
                                          } else if (
                                            ["xls", "xlsx"].includes(
                                              fileExtension
                                            )
                                          ) {
                                            fileIcon = (
                                              <DescriptionIcon fontSize="small" />
                                            );
                                          }

                                          return (
                                            <Paper
                                              key={index}
                                              elevation={1}
                                              sx={{
                                                p: 1.5,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 0.5,
                                                backgroundColor: colors.grey[50],
                                                border: `1px solid ${colors.grey[200]}`,
                                                borderRadius: 1.5,
                                                minHeight: 80,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                  elevation: 2,
                                                  borderColor:
                                                    colors.primary.main,
                                                },
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  color: colors.primary.main,
                                                  mb: 0.5,
                                                }}
                                              >
                                                {fileIcon}
                                              </Box>
                                              <Box
                                                sx={{
                                                  textAlign: "center",
                                                  flex: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: 500,
                                                    color: colors.grey[800],
                                                    mb: 0.25,
                                                    wordBreak: "break-word",
                                                    overflow: "hidden",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    lineHeight: 1.2,
                                                    fontSize: "0.75rem",
                                                  }}
                                                  title={fileName}
                                                >
                                                  {fileName}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: colors.grey[500],
                                                    textTransform: "uppercase",
                                                    fontSize: "0.65rem",
                                                  }}
                                                >
                                                  {fileExtension}
                                                </Typography>
                                              </Box>

                                              <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={
                                                  <VisibilityIcon fontSize="small" />
                                                }
                                                onClick={() =>
                                                  viewUploadedFile(
                                                    fileUrl,
                                                    fileName
                                                  )
                                                }
                                                sx={{
                                                  borderColor:
                                                    colors.primary.main,
                                                  color: colors.primary.main,
                                                  fontSize: "0.7rem",
                                                  py: 0.25,
                                                  px: 1,
                                                  minHeight: 24,
                                                  "&:hover": {
                                                    backgroundColor: alpha(
                                                      colors.primary.main,
                                                      0.1
                                                    ),
                                                    borderColor:
                                                      colors.primary.dark,
                                                  },
                                                }}
                                              >
                                                View
                                              </Button>
                                            </Paper>
                                          );
                                        })}
                                    </Box>
                                  </Box>
                                )}
                              {applicationStage === '21' && (
                                <>
                                  <Box sx={{ mt: 2, mb: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: colors.grey[700],
                                        mb: 1,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Root Cause Analysis:
                                    </Typography>
                                    <TextField
                                      multiline
                                      rows={3}
                                      fullWidth
                                      value={rootCause[nc.id] || ""}
                                      onChange={(e) =>
                                        setRootCause((prev) => ({
                                          ...prev,
                                          [nc.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="Enter root cause analysis..."
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        mb: 1,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                        },
                                      }}
                                    />
                                    <Button
                                      component="label"
                                      variant="outlined"
                                      startIcon={<CloudUploadIcon />}
                                      size="small"
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                      }}
                                    >
                                      Upload Root Cause Files
                                      <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                        onChange={(e) =>
                                          handleRootCauseFileUpload(nc.id, e)
                                        }
                                      />
                                    </Button>
                                    {rootCauseFiles[nc.id] &&
                                      rootCauseFiles[nc.id].length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                          {rootCauseFiles[nc.id].map((file, idx) => (
                                            <Chip
                                              key={idx}
                                              label={file.fileName}
                                              size="small"
                                              onDelete={() => {
                                                setRootCauseFiles((prev) => ({
                                                  ...prev,
                                                  [nc.id]: prev[nc.id].filter(
                                                    (_, i) => i !== idx
                                                  ),
                                                }));
                                              }}
                                              sx={{ mr: 1, mt: 1 }}
                                            />
                                          ))}
                                        </Box>
                                      )}
                                  </Box>


                                  <Box sx={{ mt: 2, mb: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: colors.grey[700],
                                        mb: 1,
                                        fontWeight: 600,
                                      }}
                                    >
                                      Corrective Action:
                                    </Typography>
                                    <TextField
                                      multiline
                                      rows={3}
                                      fullWidth
                                      value={correctiveAction[nc.id] || ""}
                                      onChange={(e) =>
                                        setCorrectiveAction((prev) => ({
                                          ...prev,
                                          [nc.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="Enter corrective action..."
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        mb: 1,
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                        },
                                      }}
                                    />
                                    <Button
                                      component="label"
                                      variant="outlined"
                                      startIcon={<CloudUploadIcon />}
                                      size="small"
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                      }}
                                    >
                                      Upload Corrective Action Files
                                      <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                        onChange={(e) =>
                                          handleCorrectiveActionFileUpload(nc.id, e)
                                        }
                                      />
                                    </Button>
                                    {correctiveActionFiles[nc.id] &&
                                      correctiveActionFiles[nc.id].length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                          {correctiveActionFiles[nc.id].map(
                                            (file, idx) => (
                                              <Chip
                                                key={idx}
                                                label={file.fileName}
                                                size="small"
                                                onDelete={() => {
                                                  setCorrectiveActionFiles(
                                                    (prev) => ({
                                                      ...prev,
                                                      [nc.id]: prev[nc.id].filter(
                                                        (_, i) => i !== idx
                                                      ),
                                                    })
                                                  );
                                                }}
                                                sx={{ mr: 1, mt: 1 }}
                                              />
                                            )
                                          )}
                                        </Box>
                                      )}
                                  </Box>


                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      mt: 2,
                                    }}
                                  >
                                    {(nc.stage === "" || nc.stage === "1" || nc.stage === "2" || nc.stage === null) && nc.isclsosed === "False" ? (
                                      <Button
                                        variant="contained"
                                        onClick={() =>
                                          saveNCRemarks(nc.id, ncRemarks[nc.id] || "")
                                        }
                                        disabled={savingRemarks[nc.id]}
                                        startIcon={
                                          savingRemarks[nc.id] ? (
                                            <CircularProgress
                                              size={16}
                                              color="inherit"
                                            />
                                          ) : (
                                            <SaveIcon />
                                          )
                                        }
                                        sx={{
                                          minWidth: 120,
                                          backgroundColor: colors.success.main,
                                          "&:hover": {
                                            backgroundColor: colors.success.dark,
                                          },
                                          "&:disabled": {
                                            backgroundColor: colors.grey[300],
                                          },
                                        }}
                                      >
                                        {savingRemarks[nc.id] ? "Saving..." : "Save"}
                                      </Button>
                                    ) : null}
                                  </Box>
                                </>
                              )}
                              {/* Reply History Table */}
                              {nc.ncreply && nc.ncreply.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 3,
                                    pt: 3,
                                    borderTop: `1px solid ${colors.grey[200]}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      mb: 2,
                                      cursor: "pointer",
                                      p: 1.5,
                                      borderRadius: 2,
                                      backgroundColor: alpha(
                                        colors.primary.main,
                                        0.05
                                      ),
                                      border: `1px solid ${alpha(
                                        colors.primary.main,
                                        0.2
                                      )}`,
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          colors.primary.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                    onClick={() => {
                                      setExpandedReplyHistory((prev) => ({
                                        ...prev,
                                        [nc.id]: !prev[nc.id],
                                      }));
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <ReplyIcon
                                        fontSize="small"
                                        sx={{ color: colors.primary.main }}
                                      />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 600,
                                          color: colors.primary.main,
                                        }}
                                      >
                                        Reply History ({nc.ncreply.length})
                                      </Typography>
                                    </Box>
                                    {expandedReplyHistory[nc.id] ? (
                                      <ExpandLessIcon
                                        sx={{ color: colors.primary.main }}
                                      />
                                    ) : (
                                      <ExpandMoreIcon
                                        sx={{ color: colors.primary.main }}
                                      />
                                    )}
                                  </Box>

                                  {expandedReplyHistory[nc.id] && (
                                    <TableContainer
                                      component={Paper}
                                      sx={{
                                        mt: 2,
                                        border: `1px solid ${colors.grey[200]}`,
                                      }}
                                    >
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow
                                            sx={{
                                              backgroundColor: colors.grey[100],
                                            }}
                                          >
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "5%",
                                              }}
                                            >
                                              #
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "10%",
                                              }}
                                            >
                                              Reply By
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "25%",
                                              }}
                                            >
                                              Reply Date
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "25%",
                                              }}
                                            >
                                              Assessor Reply
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "18%",
                                              }}
                                            >
                                              Root Cause
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "12%",
                                              }}
                                            >
                                              Root Cause Docs
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "18%",
                                              }}
                                            >
                                              Corrective Action
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "12%",
                                              }}
                                            >
                                              Action Docs
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontWeight: 700,
                                                width: "10%",
                                                textAlign: "center",
                                              }}
                                            >
                                              Actions
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {nc.ncreply.map((reply, replyIndex) => (
                                            <TableRow
                                              key={reply.id}
                                              sx={{
                                                "&:hover": {
                                                  backgroundColor:
                                                    colors.grey[50],
                                                },
                                                backgroundColor:
                                                  reply.replyby === "Assessor"
                                                    ? alpha(
                                                      colors.primary.main,
                                                      0.02
                                                    )
                                                    : alpha(
                                                      colors.secondary.main,
                                                      0.02
                                                    ),
                                              }}
                                            >
                                              <TableCell>
                                                {replyIndex + 1}
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={reply.replyby}
                                                  size="small"
                                                  sx={{
                                                    bgcolor:
                                                      reply.replyby === "Assessor"
                                                        ? colors.primary.main
                                                        : colors.secondary.main,
                                                    color: "white",
                                                    fontWeight: 600,
                                                  }}
                                                />
                                              </TableCell>
                                              <TableCell>
                                                {new Date(
                                                  reply.insertdate
                                                ).toLocaleDateString()}
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="body2">
                                                  {reply.replyRemarks || "-"}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ whiteSpace: "pre-wrap" }}
                                                >
                                                  {reply.rootcause || "-"}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                {reply.rootcauseupload ? (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexWrap: "wrap",
                                                      gap: 1,
                                                    }}
                                                  >
                                                    {reply.rootcauseupload
                                                      .split("~")
                                                      .filter(Boolean)
                                                      .map((doc, idx) => {
                                                        const docLower =
                                                          doc.toLowerCase();
                                                        const isImage =
                                                          docLower.match(
                                                            /\.(jpg|jpeg|png|gif|webp)$/
                                                          );
                                                        const isPdf =
                                                          docLower.endsWith(
                                                            ".pdf"
                                                          );
                                                        const isExcel =
                                                          docLower.match(
                                                            /\.(xlsx|xls)$/
                                                          );

                                                        return (
                                                          <WorkingSpaceDocument
                                                            key={idx}
                                                            doc={doc}
                                                            index={idx}
                                                            isImage={isImage}
                                                            isPdf={isPdf}
                                                            isExcel={isExcel}
                                                            colors={colors}
                                                            onImageClick={
                                                              isImage
                                                                ? handleOpenLightbox
                                                                : null
                                                            }
                                                          />
                                                        );
                                                      })}
                                                  </Box>
                                                ) : (
                                                  "-"
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ whiteSpace: "pre-wrap" }}
                                                >
                                                  {reply.correctiveAction || "-"}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                {reply.correctiveActionupload ? (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexWrap: "wrap",
                                                      gap: 1,
                                                    }}
                                                  >
                                                    {reply.correctiveActionupload
                                                      .split("~")
                                                      .filter(Boolean)
                                                      .map((doc, idx) => {
                                                        const docLower =
                                                          doc.toLowerCase();
                                                        const isImage =
                                                          docLower.match(
                                                            /\.(jpg|jpeg|png|gif|webp)$/
                                                          );
                                                        const isPdf =
                                                          docLower.endsWith(
                                                            ".pdf"
                                                          );
                                                        const isExcel =
                                                          docLower.match(
                                                            /\.(xlsx|xls)$/
                                                          );

                                                        return (
                                                          <WorkingSpaceDocument
                                                            key={idx}
                                                            doc={doc}
                                                            index={idx}
                                                            isImage={isImage}
                                                            isPdf={isPdf}
                                                            isExcel={isExcel}
                                                            colors={colors}
                                                            onImageClick={
                                                              isImage
                                                                ? handleOpenLightbox
                                                                : null
                                                            }
                                                          />
                                                        );
                                                      })}
                                                  </Box>
                                                ) : (
                                                  "-"
                                                )}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: "center" }}>
                                                <Button
                                                  variant="outlined"
                                                  color="error"
                                                  size="small"
                                                  startIcon={<DeleteIcon />}
                                                  onClick={() => handleDeleteNCReply(reply.id)}
                                                  sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    px: 2,
                                                    py: 0.5,
                                                    fontSize: "0.75rem",
                                                    "&:hover": {
                                                      backgroundColor: alpha(colors.error.main, 0.1),
                                                    }
                                                  }}
                                                >
                                                  Delete
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ) : nc.isclsosed === "False" ? (
                            // Show reply input form with acceptance question
                            <Box>
                              {/* Acceptance Question - Show on page load if no reply saved, or when editing */}
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    color: colors.grey[700],
                                  }}
                                >
                                  Do you accept this NC?
                                </Typography>

                                {/* Informational text about saved replies */}
                                <Box
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: alpha(colors.primary.main, 0.05),
                                    border: `1px solid ${alpha(colors.primary.main, 0.2)}`,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <InfoIcon
                                    sx={{
                                      color: colors.primary.main,
                                      fontSize: 20
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colors.primary.main,
                                      fontWeight: 500,
                                    }}
                                  >
                                    💡 Note: Your saved reply will appear in the reply dropdown/history section for future reference.
                                  </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                  <Button
                                    variant={
                                      labAcceptance[nc.id] === true
                                        ? "contained"
                                        : "outlined"
                                    }
                                    color="success"
                                    onClick={() =>
                                      handleLabAcceptanceChange(nc.id, true)
                                    }
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      ...(labAcceptance[nc.id] === true && {
                                        backgroundColor: colors.success.main,
                                        "&:hover": {
                                          backgroundColor: colors.success.dark,
                                        },
                                      }),
                                    }}
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    variant={
                                      labAcceptance[nc.id] === false
                                        ? "contained"
                                        : "outlined"
                                    }
                                    color="error"
                                    onClick={() =>
                                      handleLabAcceptanceChange(nc.id, false)
                                    }
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      ...(labAcceptance[nc.id] === false && {
                                        backgroundColor: colors.error.main,
                                        "&:hover": {
                                          backgroundColor: colors.error.dark,
                                        },
                                      }),
                                    }}
                                  >
                                    No
                                  </Button>
                                </Box>
                              </Box>

                              {/* Show text box and file upload for both Yes and No */}
                              {(labAcceptance[nc.id] === true ||
                                labAcceptance[nc.id] === false) && (
                                  <>
                                    {/* File Upload Section - Show for both "Yes" and "No" */}
                                    {(labAcceptance[nc.id] === true ||
                                      labAcceptance[nc.id] === false) && (
                                        <Box sx={{ mt: 3 }}>
                                          <Typography
                                            variant="subtitle2"
                                            sx={{
                                              mb: 2,
                                              fontWeight: 600,
                                              color: colors.grey[700],
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <AttachFileIcon fontSize="small" />
                                            {labAcceptance[nc.id] === true
                                              ? "SUPPORTING DOCUMENTS:"
                                              : "EVIDENCE/SUPPORTING DOCUMENTS:"}
                                          </Typography>

                                          {/* Upload Button */}
                                          <Box sx={{ mb: 2 }}>
                                            <input
                                              accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                                              id={`file-upload-${nc.id}`}
                                              type="file"
                                              multiple
                                              style={{ display: "none" }}
                                              onChange={(e) =>
                                                handleFileUpload(nc.id, e)
                                              }
                                              disabled={uploadingFiles[nc.id]}
                                            />
                                            <label htmlFor={`file-upload-${nc.id}`}>
                                              <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<CloudUploadIcon />}
                                                disabled={uploadingFiles[nc.id]}
                                                sx={{
                                                  borderColor: colors.primary.main,
                                                  color: colors.primary.main,
                                                  "&:hover": {
                                                    borderColor: colors.primary.dark,
                                                    backgroundColor: alpha(
                                                      colors.primary.main,
                                                      0.1
                                                    ),
                                                  },
                                                }}
                                              >
                                                {uploadingFiles[nc.id]
                                                  ? "Uploading..."
                                                  : "Upload Files"}
                                              </Button>
                                            </label>

                                            {uploadProgress[nc.id] > 0 && (
                                              <Box sx={{ mt: 1, width: "100%" }}>
                                                <LinearProgress
                                                  variant="determinate"
                                                  value={uploadProgress[nc.id]}
                                                  sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                  }}
                                                />
                                                <Typography
                                                  variant="caption"
                                                  sx={{ mt: 0.5, display: "block" }}
                                                >
                                                  {uploadProgress[nc.id]}% uploaded
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>

                                          {/* Uploaded Files List */}
                                          {uploadedFiles[nc.id] &&
                                            uploadedFiles[nc.id].length > 0 && (
                                              <Box>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: colors.grey[600],
                                                    mb: 1.5,
                                                    display: "block",
                                                  }}
                                                >
                                                  Uploaded Files:
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                      "repeat(auto-fit, minmax(180px, 1fr))",
                                                    gap: 1.5,
                                                    maxWidth: "100%",
                                                  }}
                                                >
                                                  {uploadedFiles[nc.id].map(
                                                    (file, index) => (
                                                      <Paper
                                                        key={index}
                                                        elevation={1}
                                                        sx={{
                                                          p: 1.5,
                                                          display: "flex",
                                                          flexDirection: "column",
                                                          alignItems: "center",
                                                          gap: 0.5,
                                                          backgroundColor:
                                                            colors.grey[50],
                                                          border: `1px solid ${colors.grey[200]}`,
                                                          borderRadius: 1.5,
                                                          minHeight: 70,
                                                          transition: "all 0.2s ease",
                                                          "&:hover": {
                                                            elevation: 2,
                                                            borderColor:
                                                              colors.primary.main,
                                                          },
                                                        }}
                                                      >
                                                        <AttachFileIcon
                                                          fontSize="small"
                                                          color="primary"
                                                        />
                                                        <Typography
                                                          variant="caption"
                                                          sx={{
                                                            fontWeight: 500,
                                                            textAlign: "center",
                                                            wordBreak: "break-word",
                                                            overflow: "hidden",
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient:
                                                              "vertical",
                                                            lineHeight: 1.2,
                                                            fontSize: "0.75rem",
                                                            mb: 0.5,
                                                          }}
                                                          title={file.fileName}
                                                        >
                                                          {file.fileName}
                                                        </Typography>
                                                        <Button
                                                          size="small"
                                                          startIcon={
                                                            <DeleteIcon fontSize="small" />
                                                          }
                                                          onClick={() =>
                                                            removeUploadedFile(
                                                              nc.id,
                                                              index
                                                            )
                                                          }
                                                          sx={{
                                                            color: colors.error.main,
                                                            fontSize: "0.7rem",
                                                            py: 0.25,
                                                            px: 0.75,
                                                            minHeight: 22,
                                                            "&:hover": {
                                                              backgroundColor: alpha(
                                                                colors.error.main,
                                                                0.1
                                                              ),
                                                            },
                                                          }}
                                                        >
                                                          Remove
                                                        </Button>
                                                      </Paper>
                                                    )
                                                  )}
                                                </Box>
                                              </Box>
                                            )}
                                        </Box>
                                      )}

                                    {/* Corrective Action Text Field - Show for both Yes and No */}
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        mb: 1,
                                        mt: 3,
                                        fontWeight: 600,
                                        color: colors.grey[700],
                                      }}
                                    >
                                      CORRECTIVE ACTION PROPOSED BY THE LABORATORY:
                                      {(labAcceptance[nc.id] === true ||
                                        labAcceptance[nc.id] === false) && (
                                          <span
                                            style={{
                                              color: colors.error.main,
                                              marginLeft: "4px",
                                            }}
                                          >
                                            *
                                          </span>
                                        )}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 2,
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <TextField
                                        fullWidth
                                        required={
                                          labAcceptance[nc.id] === true ||
                                          labAcceptance[nc.id] === false
                                        }
                                        multiline
                                        rows={3}
                                        placeholder={
                                          labAcceptance[nc.id] === true
                                            ? "Enter Corrective Action to this non-conformity... (Required)"
                                            : "Enter your response/explanation for this non-conformity... (Required)"
                                        }
                                        value={ncReplies[nc.id] || ""}
                                        onChange={(e) =>
                                          handleReplyChange(nc.id, e.target.value)
                                        }
                                        variant="outlined"
                                        error={
                                          (labAcceptance[nc.id] === true ||
                                            labAcceptance[nc.id] === false) &&
                                          !ncReplies[nc.id]?.trim()
                                        }
                                        helperText={
                                          (labAcceptance[nc.id] === true ||
                                            labAcceptance[nc.id] === false) &&
                                            !ncReplies[nc.id]?.trim()
                                            ? "This field is required"
                                            : ""
                                        }
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            backgroundColor: "white",
                                            "&:hover": {
                                              borderColor: colors.primary.main,
                                            },
                                            "&.Mui-focused": {
                                              borderColor: colors.primary.main,
                                            },
                                            "&.Mui-error": {
                                              borderColor: colors.error.main,
                                            },
                                          },
                                        }}
                                      />
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        {editingReplies[nc.id] && (
                                          <Button
                                            variant="outlined"
                                            onClick={() => cancelEditMode(nc.id)}
                                            disabled={submittingReplies[nc.id]}
                                            sx={{
                                              minWidth: 80,
                                              borderColor: colors.grey[400],
                                              color: colors.grey[600],
                                              "&:hover": {
                                                borderColor: colors.grey[600],
                                                backgroundColor: alpha(
                                                  colors.grey[600],
                                                  0.1
                                                ),
                                              },
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        )}
                                        <Button
                                          variant="contained"
                                          startIcon={
                                            submittingReplies[nc.id] ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : (
                                              <SaveIcon />
                                            )
                                          }
                                          onClick={() => saveNCReply(nc.id)}
                                          disabled={
                                            submittingReplies[nc.id]
                                            // Allow saving with empty text for both Yes and No
                                          }
                                          sx={{
                                            minWidth: 120,
                                            backgroundColor: colors.success.main,
                                            "&:hover": {
                                              backgroundColor: colors.success.dark,
                                            },
                                            "&:disabled": {
                                              backgroundColor: colors.grey[300],
                                            },
                                          }}
                                        >
                                          {submittingReplies[nc.id]
                                            ? "Saving..."
                                            : "Save"}
                                        </Button>
                                      </Box>
                                    </Box>
                                  </>
                                )}
                            </Box>
                          ) : (
                            // NC is closed - show message
                            <Box sx={{
                              p: 2,
                              backgroundColor: alpha(colors.grey[500], 0.05),
                              borderRadius: 2,
                              border: `1px solid ${alpha(colors.grey[500], 0.2)}`,
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                This non-conformity is closed and cannot be replied to.
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}

              {(isStage3 || hasNCs) && (
                <>
                  {/* 🔹 Stage 3 Alert */}
                  {isStage3 && (
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Assessment Completed
                        </Typography>
                        <Typography variant="body2">
                          This assessment has been completed. Reply editing and
                          submission are no longer available.
                        </Typography>
                      </Alert>
                    </Box>
                  )}

                  {/* 🔹 Submit Button (Only show if not Stage 3) */}
                  {!isStage3 && hasNCs && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        {!canSubmit && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Please save all NC replies before submission
                          </Typography>
                        )}
                        {applicationStage === '20' && (
                          <>
                          <Button
                            variant="contained"
                            disabled={!canSubmit}
                            startIcon={<ReplyIcon />}
                            onClick={handleStageUpdate}
                            sx={{
                              background: allHaveAcceptanceDecisions
                                ? `linear-gradient(135deg, ${colors.success.main}, ${colors.primary.main})`
                                : colors.grey[300],
                              color: allHaveAcceptanceDecisions
                                ? "white"
                                : colors.grey[500],
                              px: 4,
                              py: 1.5,
                              borderRadius: 3,
                              fontWeight: 600,
                              textTransform: "none",
                              boxShadow: allHaveAcceptanceDecisions
                                ? `0 4px 16px ${alpha(colors.success.main, 0.3)}`
                                : "none",
                              "&:hover": allHaveAcceptanceDecisions
                                ? {
                                  boxShadow: `0 8px 24px ${alpha(
                                    colors.success.main,
                                    0.4
                                  )}`,
                                  transform: "translateY(-2px)",
                                }
                                : {},
                              "&:disabled": {
                                backgroundColor: colors.grey[300],
                                color: colors.grey[500],
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {allHaveAcceptanceDecisions
                              ? "Submit My Reply"
                              : `Submit My Reply (${nonConformities.filter(
                                (nc) =>
                                  labAcceptance[nc.id] !== null &&
                                  labAcceptance[nc.id] !== undefined
                              ).length
                              }/${nonConformities.length} decided)`}
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<Refresh />}
                            sx={{ml: 1}}
                            onClick={() => window.location.reload()}
                          >
                            Refresh
                          </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}


                  {/* Send to Assessor Button - Show when all NCs have Lab replies */}
                  {checkAllNCsHaveLabReplies() && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          All non-conformities have been replied. Send to
                          assessor for review.
                        </Typography>
                        <Button
                          variant="contained"
                          disabled={sendingToAssessor}
                          startIcon={
                            sendingToAssessor ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <SendIcon />
                            )
                          }
                          onClick={handleSendToAssessorConfirmation}
                          sx={{
                            background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                            color: "white",
                            px: 4,
                            py: 1.5,
                            borderRadius: 3,
                            fontWeight: 600,
                            textTransform: "none",
                            boxShadow: `0 4px 16px ${alpha(
                              colors.primary.main,
                              0.3
                            )}`,
                            "&:hover": {
                              boxShadow: `0 8px 24px ${alpha(
                                colors.primary.main,
                                0.4
                              )}`,
                              transform: "translateY(-2px)",
                            },
                            "&:disabled": {
                              backgroundColor: colors.grey[300],
                              color: colors.grey[500],
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {sendingToAssessor
                            ? "Sending..."
                            : "Send to Assessor"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          onClick={handleCloseLightbox}
        >
          {lightboxImage && (
            <img
              src={lightboxImage}
              alt="Enlarged view"
              style={{
                maxWidth: "90%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </Box>
      )}

      {/* Send to Assessor Confirmation Dialog */}
      <Dialog
        open={sendToAssessorConfirmDialogOpen}
        onClose={() => setSendToAssessorConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
          <SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Send to Assessor Confirmation
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Are you sure you want to send all replies to the Assessor?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will notify the assessor that all non-conformities have been replied to and are ready for review. You will be redirected to the laboratory dashboard after confirmation.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={() => setSendToAssessorConfirmDialogOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSendToAssessor}
            variant="contained"
            color="primary"
            disabled={sendingToAssessor}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {sendingToAssessor ? 'Sending...' : 'Yes, Send to Assessor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete NC Reply Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', textAlign: 'center' }}>
          <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delete NC Reply Confirmation
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Are you sure you want to delete this NC reply?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The NC reply will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={() => setDeleteConfirmDialogOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedDeleteNCReply}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Yes, Delete Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Working Space Document Component
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // For PDF/Excel files
  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          Click to view
        </Typography>
      </Box>
    );
  }

  // For images
  return (
    <Box
      sx={{
        width: 80,
        height: 80,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Evidence ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

export default NCReply;
